import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Divider, IconButton, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { Stop } from '../../types';
import { serifVoice } from '../../theme';
import { MODE_LABELS, formatDay, legIcon, shortTime } from '../../lib/plan';

export const DRAG_TYPE = 'application/x-roamoir-stop';

/** Where a card can be sent from its menu: candidates (null) or a day. */
export interface MoveTarget {
  dayId: string | null;
  label: string;
}

export type CardVariant = 'placed' | 'suggested' | 'candidate';

interface Props {
  stop: Stop;
  variant: CardVariant;
  targets: MoveTarget[];
  /** Candidates panel: where the last plan suggested it, if anywhere. */
  suggestedLabel?: string | null;
  onMove: (stopId: string, dayId: string | null) => void;
  /** Another card was dropped onto this one: insert it before this. */
  onDropBefore?: (stopId: string) => void;
  onKeep?: (stop: Stop) => void;
  onReject?: (stop: Stop) => void;
  onToggleMustSee: (stop: Stop) => void;
  onRemove: (stop: Stop) => void;
}

const ACCEPT = (e: React.DragEvent) => e.dataTransfer.types.includes(DRAG_TYPE);

/**
 * One place. Desktop moves it by dragging (native HTML5 drag and drop — no
 * library); a phone or a keyboard has no drag, so every card also carries a
 * "Move to" menu that does the same thing.
 */
export default function PlaceCard({
  stop,
  variant,
  targets,
  suggestedLabel,
  onMove,
  onDropBefore,
  onKeep,
  onReject,
  onToggleMustSee,
  onRemove,
}: Props) {
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [over, setOver] = useState(false);
  const suggested = variant === 'suggested';
  const here = variant === 'candidate' ? null : variant === 'suggested' ? stop.suggested_day_id : stop.day_id;

  return (
    <Box
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_TYPE, stop.stop_id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        if (onDropBefore && ACCEPT(e)) {
          e.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        if (!onDropBefore) return;
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        const id = e.dataTransfer.getData(DRAG_TYPE);
        if (id && id !== stop.stop_id) onDropBefore(id);
      }}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        minHeight: 44,
        py: 0.75,
        pl: 0.5,
        pr: 0.5,
        borderRadius: 1.5,
        border: suggested ? '1.5px dashed #D4805E' : '1px solid',
        borderColor: suggested ? undefined : 'divider',
        bgcolor: suggested ? '#FDF6F2' : 'background.paper',
        cursor: 'grab',
        // The insertion point: a line above the card being dropped onto.
        boxShadow: over ? (t) => `0 -3px 0 0 ${t.palette.secondary.main}` : 'none',
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <DragIndicatorIcon fontSize="small" sx={{ color: 'text.disabled', mt: 0.5, flex: 'none' }} />
      <Box sx={{ fontSize: '1rem', lineHeight: 1.6, width: 20, textAlign: 'center', flex: 'none' }}>{stop.icon ?? '📍'}</Box>
      <Box sx={{ flex: 1, minWidth: 0, py: 0.25 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
          <Typography
            component={RouterLink}
            to={`/stops/${stop.stop_id}`}
            draggable={false}
            variant="body2"
            sx={{ fontWeight: 600, color: 'text.primary', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
          >
            {stop.title}
          </Typography>
          {stop.visit_hours ? (
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', flex: 'none' }}>
              {stop.visit_hours} h
            </Typography>
          ) : null}
        </Box>
        {variant === 'candidate' ? (
          <>
            {stop.city || suggestedLabel ? (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {stop.city}
                {suggestedLabel ? (
                  <Box component="span" sx={{ color: 'secondary.dark', fontWeight: 600 }}>
                    {stop.city ? ' · ' : ''}Suggested for {suggestedLabel}
                  </Box>
                ) : null}
              </Typography>
            ) : null}
            {stop.plan_note ? (
              <Typography variant="caption" sx={{ ...serifVoice, display: 'block', color: 'text.secondary', mt: 0.25, lineHeight: 1.45 }}>
                {stop.plan_note}
              </Typography>
            ) : null}
          </>
        ) : stop.city && variant === 'suggested' ? (
          <Typography variant="caption" color="secondary.dark">
            Suggested · {stop.city}
          </Typography>
        ) : null}
      </Box>

      {suggested && onKeep && onReject ? (
        <>
          <Tooltip title="Keep on this day">
            <IconButton
              size="small"
              aria-label={`Keep ${stop.title}`}
              onClick={() => onKeep(stop)}
              sx={{ bgcolor: 'primary.main', color: '#FFFFFF', '&:hover': { bgcolor: 'primary.dark' } }}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Back to candidates">
            <IconButton size="small" aria-label={`Drop ${stop.title}`} onClick={() => onReject(stop)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ) : (
        <Tooltip title={stop.must_see ? 'Must-see' : 'Mark as must-see'}>
          <IconButton size="small" onClick={() => onToggleMustSee(stop)} aria-label={stop.must_see ? 'Must-see' : 'Mark as must-see'}>
            {stop.must_see ? (
              <StarIcon fontSize="small" sx={{ color: 'secondary.main' }} />
            ) : (
              <StarBorderIcon fontSize="small" sx={{ color: 'text.disabled' }} />
            )}
          </IconButton>
        </Tooltip>
      )}

      <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label={`Move or remove ${stop.title}`}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        <MenuItem disabled dense>
          <ListItemText secondary="Move to" />
        </MenuItem>
        {targets
          .filter((t) => t.dayId !== here || variant === 'suggested')
          .map((t) => (
            <MenuItem
              key={t.dayId ?? 'candidates'}
              dense
              onClick={() => {
                setMenuAnchor(null);
                onMove(stop.stop_id, t.dayId);
              }}
            >
              {t.label}
            </MenuItem>
          ))}
        <Divider />
        <MenuItem dense component={RouterLink} to={`/stops/${stop.stop_id}`}>
          Open details
        </MenuItem>
        <MenuItem
          dense
          onClick={() => {
            setMenuAnchor(null);
            onRemove(stop);
          }}
          sx={{ color: 'error.main' }}
        >
          Remove
        </MenuItem>
      </Menu>
    </Box>
  );
}

/** A transport leg. Open (no mode) legs are the ones still to decide. */
export function LegCard({
  stop,
  onOpen,
  dateLabel,
  dayDate,
}: {
  stop: Stop;
  onOpen: (stop: Stop) => void;
  dateLabel?: string;
  /** The day it is shown on, so an arrival on a later date can say which. */
  dayDate?: string;
}) {
  const open = !stop.leg_mode;
  const dep = shortTime(stop.depart_time);
  const arr = shortTime(stop.arrive_time);
  const later = stop.arrive_date && dayDate && stop.arrive_date !== dayDate ? `${formatDay(stop.arrive_date)} ` : '';
  const times = dep || arr ? `${dep ?? '?'} → ${later}${arr ?? '?'}` : null;
  return (
    <Box
      component="button"
      type="button"
      onClick={() => onOpen(stop)}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        width: '100%',
        minHeight: 44,
        p: 1,
        textAlign: 'left',
        font: 'inherit',
        color: 'text.primary',
        cursor: 'pointer',
        borderRadius: 1.5,
        border: open ? '1.5px dashed' : '1px dashed',
        borderColor: open ? 'secondary.main' : 'text.disabled',
        bgcolor: open ? '#FBF1EC' : 'transparent',
      }}
    >
      <Box sx={{ width: 20, textAlign: 'center', flex: 'none' }}>{legIcon(stop.leg_mode)}</Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {stop.title}
        </Typography>
        <Typography variant="caption" sx={{ color: open ? 'secondary.dark' : 'text.secondary' }}>
          {open
            ? `How not decided${stop.decide_by ? ` · decide by ${formatDay(stop.decide_by)}` : ''}${dateLabel ? ` · ${dateLabel}` : ''}`
            : [MODE_LABELS[stop.leg_mode!], times, stop.context?.split('\n')[0]].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, alignSelf: 'center' }}>
        {open ? 'Set it' : 'Edit'}
      </Typography>
    </Box>
  );
}
